/**
 * Сериализация массива в FormData: файлы и скаляры — без индексов
 * (parentKey[array][]), объекты — с индексом (key[i][field]), как того
 * требуют Rails nested attributes.
 * @param formData - целевой FormData
 * @param fullKey - полный ключ поля
 * @param value - массив значений
 */
const appendArray = (
  formData: FormData,
  fullKey: string,
  value: unknown[]
): void => {
  // Если массив пустой, добавляем пустое значение
  if (value.length === 0) {
    formData.append(`${fullKey}[]`, '');

    return;
  }

  value.forEach((item, index) => {
    // Проверяем сначала является ли элеммент файлом, а потом уже объектом.
    // Blob, а не File: File наследует Blob, а голый Blob иначе уходит в ветку
    // «объект», где for..in не даёт ни одного ключа — и поле молча пропадает
    if (item instanceof Blob) {
      formData.append(`${fullKey}[]`, item);
    } else if (typeof item === 'object' && item !== null) {
      const nestedFormData = formatFormData(
        item as Record<string, unknown>,
        `${fullKey}[${index}]`
      );
      for (const [name, nestedValue] of nestedFormData.entries()) {
        formData.append(name, nestedValue);
      }
    } else {
      formData.append(`${fullKey}[]`, String(item));
    }
  });
};

/**
 * Форматтер для Axios, чтобы массивы отправлялись в формате
 * parentKey[array][] (без индексов), как ожидают Rails.
 * @param data - данные
 * @param parentKey - префикс ключа (для вложенных объектов)
 */
export const formatFormData = <T extends Record<string, unknown>>(
  data: T,
  parentKey?: string
): FormData => {
  const formData = new FormData();

  for (const key in data) {
    const fullKey = parentKey ? `${parentKey}[${key}]` : key;
    const value = data[key];

    // undefined = «поля нет»: иначе в FormData уходит строка "undefined",
    // и бэк принимает её за значение (например, за signed id вложения)
    if (value === undefined) continue;

    if (Array.isArray(value)) {
      appendArray(formData, fullKey, value);
    } else if (value instanceof Blob) {
      // Blob, а не File: голый Blob (вставка из буфера, drag-n-drop, шаринг)
      // иначе попадал бы в ветку «объект» и терялся без единой ошибки —
      // на бэкенд уходил корректный multipart без поля (19038)
      formData.append(fullKey, value);
    } else if (typeof value === 'object' && value !== null) {
      // Рекурсивная обработка вложенных объектов с корректным формированием ключей
      const nestedFormData = formatFormData(
        value as Record<string, unknown>,
        fullKey
      );
      for (const [name, nestedValue] of nestedFormData.entries()) {
        formData.append(name, nestedValue);
      }
    } else {
      formData.append(fullKey, value as string);
    }
  }

  return formData;
};
